import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  constructor() {
    super();

    this.tags = this.querySelectorAll('.tag-filter');
    this.blogContainer = this.querySelector('.blog-posts-container');

    this.onTagClick = this.onTagClick.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.tags.forEach((tagEl) => {
      tagEl.addEventListener('click', this.onTagClick);
    });

    window.addEventListener('popstate', this.onPopState);

    // Load correct state on initial page load
    const initialTag = this.getTagFromUrl();
    if (initialTag) {
      this.filterBlogs(initialTag, false);
      this.setActiveTag(initialTag);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  /**
   * Handle tag click
   */
  async onTagClick(e) {
    e.preventDefault();

    const tagEl = e.currentTarget;
    const tag = tagEl.dataset.tag;
    if (!tag) return;

    await this.filterBlogs(tag, true);
    this.setActiveTag(tag);
  }

  /**
   * Remove active state from all tags
   */
  removeActive() {
    this.tags.forEach((el) => el.classList.remove('active'));
  }

  /**
   * Set active tag
   */
  setActiveTag(tag) {
    this.removeActive();
    this.tags.forEach((el) => {
      if (el.dataset.tag === tag) {
        el.classList.add('active');
      }
    });
  }

  /**
   * Get tag from URL
   */
  getTagFromUrl() {
    const match = window.location.pathname.match(/\/tagged\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : 'all';
  }

  /**
   * Handle browser back / forward
   */
  onPopState(e) {
    const tag = e.state?.tag || this.getTagFromUrl();
    this.filterBlogs(tag, false);
    this.setActiveTag(tag);
  }

  /**
   * Load blog posts filtered by tag via AJAX
   * @param {string} tag
   * @param {boolean} pushState
   */
  async filterBlogs(tag, pushState = true) {
    if (!tag || !this.blogContainer) return;

    const blogHandle = this.dataset.blogHandle;
    const url =
      tag === 'all'
        ? `/blogs/${blogHandle}`
        : `/blogs/${blogHandle}/tagged/${encodeURIComponent(tag)}`;

    this.blogContainer.classList.add('loading');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load blog posts');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newContainer = doc.querySelector('.blog-posts-container');
      if (!newContainer) return;

      this.blogContainer.innerHTML = newContainer.innerHTML;

      // Add pagination refs if needed
      this.updatePaginationRefs(newContainer);

      // Re-init PaginatedList after DOM update
      if (typeof this.init === 'function') {
        this.init();
      }

      if (pushState) {
        window.history.pushState({ tag }, '', url);
      }

      // Notify theme
      this.dispatchEvent(new CustomEvent('blog:updated', { bubbles: true }));
    } catch (error) {
      console.error('[BlogPostsList]', error);
    } finally {
      this.blogContainer.classList.remove('loading');
    }
  }

  /**
   * Inject pagination refs if missing
   */
  updatePaginationRefs(newContainer) {
    const pages = newContainer.dataset.lastPage;
    this.blogContainer?.setAttribute('data-last-page', pages);
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
